'use client';

import { ReactNode } from 'react';

/* ============ 状态徽章 ============ */
export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'gold' | 'neutral';

const toneMap: Record<Tone, string> = {
  success: 'bg-badge-success-bg text-badge-success border-badge-success-border',
  warning: 'bg-badge-warning-bg text-badge-warning border-badge-warning-border',
  danger: 'bg-badge-danger-bg text-badge-danger border-badge-danger-border',
  info: 'bg-badge-info-bg text-badge-info border-badge-info-border',
  gold: 'bg-badge-maple-bg text-badge-maple border-badge-maple-border',
  neutral: 'bg-hover text-muted border-border',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${toneMap[tone]}`}>
      {children}
    </span>
  );
}

/* ============ 统计卡 ============ */
const statColorMap: Record<string, string> = {
  success: 'bg-badge-success-bg text-badge-success',
  warning: 'bg-badge-warning-bg text-badge-warning',
  danger: 'bg-badge-danger-bg text-badge-danger',
  info: 'bg-badge-info-bg text-badge-info',
  gold: 'bg-badge-maple-bg text-badge-maple',
  neutral: 'bg-hover text-muted',
};

export function StatCard({
  label,
  value,
  sub,
  color = 'info',
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  icon: string;
}) {
  return (
    <div className="bg-card rounded-xl p-5 border border-border hover:border-faint/60 transition-colors shadow-sm">
      <div className={`w-11 h-11 rounded-xl ${statColorMap[color] ?? statColorMap.info} flex items-center justify-center mb-3 ring-1 ring-inset ring-black/5`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
      <div className="text-2xl font-bold text-foreground tracking-tight">{value}</div>
      <div className="text-sm font-medium text-secondary mt-1">{label}</div>
      {sub && <div className="text-xs text-muted mt-0.5">{sub}</div>}
    </div>
  );
}

/* ============ 页头 ============ */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-sidebar1 to-[#3d2f0b] border border-border px-6 py-5">
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-badge-maple-bg"></div>
      <div className="relative flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          {subtitle && (
            <p className="text-neutral-300 mt-1 flex items-center gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-maple animate-pulse"></span>
              {subtitle}
            </p>
          )}
        </div>
        {actions && <div className="flex gap-2">{actions}</div>}
      </div>
    </div>
  );
}

/* ============ 通用弹窗 ============ */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div
        className={`bg-card rounded-xl border border-border w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[88vh] overflow-y-auto shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="text-muted hover:text-foreground transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-border flex gap-3 justify-end">{footer}</div>}
      </div>
    </div>
  );
}

/* ============ 按钮 ============ */
export function Btn({
  children,
  onClick,
  tone = 'gold',
  size = 'md',
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  tone?: 'gold' | 'success' | 'danger' | 'warning' | 'ghost' | 'dark';
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  const map: Record<string, string> = {
    gold: 'bg-maple text-neutral-900 hover:bg-maple-light',
    success: 'bg-badge-success-bg text-badge-success border border-badge-success-border hover:brightness-110',
    danger: 'bg-badge-danger-bg text-badge-danger border border-badge-danger-border hover:brightness-110',
    warning: 'bg-badge-warning-bg text-badge-warning border border-badge-warning-border hover:brightness-110',
    ghost: 'bg-hover text-secondary hover:bg-border border border-border',
    dark: 'bg-badge-maple-bg text-badge-maple border border-badge-maple-border hover:brightness-110',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} rounded-lg font-semibold transition-all ${map[tone]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}

/* ============ 表单字段 ============ */
export function Field({ label, children, required }: { label: string; children: ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-secondary mb-1.5">
        {label} {required && <span className="text-badge-danger">*</span>}
      </label>
      {children}
    </div>
  );
}

export const inputCls =
  'w-full px-4 py-2.5 rounded-lg bg-input border border-border text-foreground placeholder:text-faint focus:border-maple focus:ring-1 focus:ring-maple outline-none text-sm';

/* ============ 区块卡片 ============ */
export function Card({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-xl border border-border overflow-hidden shadow-sm ${className ?? ''}`}>
      {(title || actions) && (
        <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3 flex-wrap bg-card-deep/50">
          {typeof title === 'string' ? <h3 className="text-base font-bold text-foreground tracking-tight">{title}</h3> : title}
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

/* ============ 表格 ============ */
export function Table({ headers, children }: { headers: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-header-row">
            {headers.map((h, i) => (
              <th key={i} className="text-left text-xs font-semibold text-secondary uppercase tracking-wider px-6 py-3 whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">{children}</tbody>
      </table>
    </div>
  );
}

/* ============ Tab 页签 ============ */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            active === tab.key ? 'bg-maple text-neutral-900 shadow-sm' : 'bg-card text-secondary border border-border hover:bg-hover hover:text-foreground'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ============ 空状态 ============ */
export function Empty({ text }: { text: string }) {
  return <div className="py-12 text-center text-faint text-sm">{text}</div>;
}
